import { createSlice } from '@reduxjs/toolkit';
import { find } from 'lodash';
import moment from 'moment';
import { proc } from '@aexae/js_tools';

import socket from '../../utils/socket';

const { date, siteId } = proc.getQueryParams();

// Parse strict des deux formats
const argDay = moment(date, 'YYYY-MM-DD', true);
const argMonth = moment(date, 'YYYY-MM', true);

// Source de vérité pour pickerDate
let initialPickerMoment;
if (argDay.isValid()) {
  initialPickerMoment = argDay; // jour exact
} else if (argMonth.isValid()) {
  initialPickerMoment = argMonth.clone().startOf('month'); // 1er du mois
} else {
  initialPickerMoment = moment().startOf('month'); // fallback : début du mois courant
}

const initialState = {
  isLoading: false,
  error: false,
  // URLSearchParams
  month: initialPickerMoment.format('YYYYMM'),
  pickerDate: initialPickerMoment.toDate(),
  siteId,
  // datas
  shifts: [],
  minMonth: undefined,
  maxMonth: undefined,
  isPublished: false,
  unpublishedMonth: undefined,
  validateAt: undefined,
  // Schedule resource
  scheduleResourceView: 'dayGridMonth',
  scheduleResourceShifts: [],
  scheduleResourceHolidays: [],
  hasEvents: false,
  // api call props
  getScheduleResourceProps: null,
  // Schedule site
  siteScheduleView: undefined,
  siteScheduleShifts: [],
  siteScheduleNames: [],
  // To switch modes
  scheduleSiteNameSaved: [],
  scheduleSiteHolidays: [],
  // api call props
  getScheduleSiteProps: null,
  // sort
  sortBy: ['datetime_start'],
  sortOrders: ['asc'],
  // filter
  filterOptions: {
    isSiteSchedule: false,
    prestations: [],
    profiles: [],
    activities: [],
    sites: []
  },
  filters: {
    unassigned: true,
    external: true,
    isSiteSchedule: false,
    prestations: [],
    profiles: [],
    activities: [],
    sites: []
  },
  siteScheduleFetched: false
};

const slice = createSlice({
  name: 'schedules',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
      state.siteScheduleFetched = false;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.siteScheduleFetched = true; // terminé (même si erreur)
      console.error(state.error);
    },

    getScheduleResource(state, action) {
      const { props, shifts, holidays, isPublished, unpublishedMonth } = action.payload;

      // On stocke un snapshot immuable des props pour éviter que des
      // mutations externes re‑changent la clé du dataset après coup.
      state.getScheduleResourceProps = { ...props };

      if (unpublishedMonth) {
        state.unpublishedMonth = unpublishedMonth;
      } else if (
        isPublished === false &&
        !unpublishedMonth &&
        moment(state.pickerDate).startOf('week').format('YYYY-MM-DD').substring(0, 7) !==
          moment(state.pickerDate).endOf('week').format('YYYY-MM-DD').substring(0, 7)
      ) {
        if (moment(state.pickerDate).startOf('week').format('YYYYMM') <= props.minMonth) {
          state.unpublishedMonth = props.minMonth;
        } else if (moment(state.pickerDate).endOf('week').format('YYYYMM') >= props.maxMonth) {
          state.unpublishedMonth = props.maxMonth;
        } else {
          state.unpublishedMonth = null;
        }
      } else {
        state.unpublishedMonth = null;
      }

      state.isLoading = false;
      state.scheduleResourceShifts = shifts;
      state.scheduleResourceHolidays = holidays;
      state.hasEvents = shifts.length;
      state.isPublished = isPublished;
      state.minMonth = props.minMonth;
      state.maxMonth = props.maxMonth;
      const shift = find(shifts, (el) => el.month === parseInt(state.month, 10));
      state.validateAt = shift ? shift.schedule_validate_at : null;
    },

    getSiteSchedule(state, action) {
      const {
        props,
        shifts,
        names,
        holidays,
        isPublished,
        unpublishedMonth,
        prestations,
        profiles,
        activities,
        sites
      } = action.payload;

      // Même principe : snapshot des props pour que TimeLines puisse
      // comparer la clé du dataset à un état figé de la requête.
      state.getScheduleSiteProps = { ...props };

      if (!state.filters.isSiteSchedule) {
        names.map((name) => (name.shifts = shifts.filter((el) => el.resource_id === name.id)));
      } else {
        sites.map((site) => (site.shifts = shifts.filter((el) => el.site_id === site.id)));
      }

      state.siteScheduleShifts = shifts;
      state.siteScheduleNames = names;
      state.scheduleSiteNameSaved = names;
      state.scheduleSiteHolidays = holidays;
      state.isPublished = isPublished;
      state.unpublishedMonth = unpublishedMonth;
      state.minMonth = props.minMonth;
      state.maxMonth = props.maxMonth;

      state.filterOptions.prestations = prestations;
      state.filterOptions.profiles = profiles;
      state.filterOptions.activities = activities;
      state.filterOptions.sites = sites;
      state.siteScheduleFetched = true; // terminé (succès)
    },

    validateSchedule(state, action) {
      state.validateAt = action.payload;
      state.isLoading = false;
    },

    setPickerDate(state, action) {
      state.pickerDate = state.pickerDate || new Date(); // Fallback sur la date actuelle si non définie

      state.isLoading = false;
      state.pickerDate = action.payload;

      // Vérification de la validité de la date
      if (moment(state.pickerDate).isValid()) {
        state.month = moment(state.pickerDate).format('YYYYMM');
      } else {
        console.error('La date pickerDate est invalide. Utilisation de la date actuelle.');
        state.month = moment().format('YYYYMM'); // Utilise la date actuelle comme fallback
      }
    },

    setResourceScheduleView(state, action) {
      state.scheduleResourceView = action.payload;
    },

    setSiteScheduleView(state, action) {
      state.siteScheduleView = action.payload;
    },

    setSchedule(state, action) {
      const schedule = action.payload;
      const idx = state.scheduleResourceShifts.findIndex((el) => el.id === schedule.id);
      if (idx >= 0) {
        state.scheduleResourceShifts[idx] = { ...state.scheduleResourceShifts[idx], ...schedule };
      }
    },

    setScheduleTypeNames(state, action) {
      const isSiteNameSchedule = action.payload;
      const copyName = [...state.scheduleSiteNameSaved];
      const copySite = state.filterOptions.sites;
      const copy = !isSiteNameSchedule ? copyName : copySite;
      if (copy.length > 0) {
        const baseFilter = (el) => (!isSiteNameSchedule ? el.resource_id : el.site_id);
        const data = [...state.siteScheduleShifts];
        copy.map((name) => (name.shifts = data.filter((el) => baseFilter(el) === name.id)));
      }
      state.siteScheduleNames = copy;
    },

    // FILTERS
    siteScheduleFilter(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },

    resetSiteScheduleFilters(state, action) {
      const isSiteSchedule = action.payload;
      state.filters = { ...initialState.filters, isSiteSchedule };
    },

    // Reset du state lors du changement d'agent
    // Réinitialise toutes les données du planning sauf le mois en cours
    resetScheduleState: (state) => {
      // Reset des données du planning
      state.scheduleResourceShifts = [];
      state.scheduleResourceHolidays = [];
      state.hasEvents = false;
      state.shifts = [];

      // Reset des états de publication
      state.isPublished = false;
      state.validateAt = null;
      state.unpublishedMonth = undefined;

      // Reset des propriétés de l'API
      state.getScheduleResourceProps = null;

      // Reset des états de chargement et d'erreur
      state.isLoading = false;
      state.error = false;

      // Reset de la vue
      state.scheduleResourceView = initialState.scheduleResourceView;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  resetScheduleState,
  resetSiteScheduleFilters,
  setPickerDate,
  setResourceScheduleView,
  setSchedule,
  siteScheduleFilter,
  setScheduleTypeNames,
  setSiteScheduleView
} = slice.actions;

export function getScheduleResource(props) {
  return async (dispatch) => {
    const { settings } = socket.env;
    const { pickerDate, contractId, datesBounds = {}, view = initialState.scheduleResourceView } = props;

    // Si pas de contractId, on ne fait pas la requête
    if (!contractId) {
      return;
    }

    props.minMonth =
      datesBounds.disableBefore &&
      moment(datesBounds.disableBefore, 'YYYY-MM-DD').format('YYYYMMDD') >
        moment().add(-settings.res_month_before_schedule, 'M').startOf('month').format('YYYYMMDD')
        ? moment(datesBounds.disableBefore, 'YYYY-MM-DD').format('YYYYMMDD')
        : moment().add(-settings.res_month_before_schedule, 'M').startOf('month').format('YYYYMMDD');

    props.maxMonth =
      datesBounds.disableAfter &&
      moment(datesBounds.disableAfter, 'YYYY-MM-DD').format('YYYYMMDD') <
        moment().add(settings.res_month_after_schedule, 'M').endOf('month').format('YYYYMMDD')
        ? moment(datesBounds.disableAfter, 'YYYY-MM-DD').format('YYYYMMDD')
        : moment().add(settings.res_month_after_schedule, 'M').endOf('month').format('YYYYMMDD');

    const params = {
      isCalendar: true,
      isWeekBetweenYears: 0,
      datetime: moment().format('YYYY-MM-DD HH:mm:ss'),
      contractId
    };

    // Semaine à cheval sur deux mois
    if (
      view === 'timeGridWeek' &&
      moment(pickerDate).startOf('week').format('YYYYMM') !== moment(pickerDate).add(6, 'd').format('YYYYMM')
    ) {
      params.dateStart = moment(pickerDate).startOf('week').format('YYYY-MM-DD');
      params.dateEnd = moment(pickerDate).add(6, 'd').format('YYYY-MM-DD');
      if (!moment(params.dateStart).isSame(params.dateEnd, 'year')) {
        params.isWeekBetweenYears = 1;
      }
    } else {
      params.month = moment(pickerDate).format('YYYYMM');
    }

    try {
      dispatch(slice.actions.startLoading());
      const result = await socket.get('/schedules', params);
      dispatch(slice.actions.getScheduleResource({ ...result, props }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSiteSchedule(props) {
  const { siteScheduleView, pickerDate } = props;

  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const { settings } = socket.env;

      switch (socket.user.role) {
        case 'client':
          // String YYYYMM : aligné sur le propType de PeriodPicker (warning React si
          // number). Les comparaisons aval restent valides (`format('YYYYMM') < minMonth`
          // est string vs string).
          props.minMonth = moment().add(-settings.cli_month_before_schedule, 'M').format('YYYYMM');
          props.maxMonth = moment().add(settings.cli_month_after_schedule, 'M').format('YYYYMM');
          break;
        case 'resource':
          props.minMonth = moment().add(-settings.res_month_before_schedule, 'M').format('YYYYMM');
          props.maxMonth = moment().add(settings.res_month_after_schedule, 'M').format('YYYYMM');
          break;
        default:
          props.minMonth = null;
          props.maxMonth = null;
      }

      const params = {
        isCalendar: true,
        siteId: props.siteId || null,
        datetime: moment().format('YYYY-MM-DD HH:mm:ss')
      };

      if (siteScheduleView === 'pHebdo') {
        if (props.minMonth !== null && moment(pickerDate).startOf('week').format('YYYYMM') < props.minMonth) {
          params.dateStart = moment(props.minMonth, 'YYYYMM').startOf('month').format('YYYY-MM-DD');
        } else {
          params.dateStart = moment(pickerDate).startOf('week').format('YYYY-MM-DD');
        }
        if (props.maxMonth !== null && moment(pickerDate).endOf('week').format('YYYYMM') > props.maxMonth) {
          params.dateEnd = moment(props.maxMonth, 'YYYYMM').endOf('month').format('YYYY-MM-DD');
        } else {
          params.dateEnd = moment(pickerDate).endOf('week').format('YYYY-MM-DD');
        }
      } else {
        params.month = moment(pickerDate).startOf('month').format('YYYYMM');
      }

      const result = await socket.get('/schedules', params);
      dispatch(slice.actions.getSiteSchedule({ ...result, props }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.stopLoading());
    }
  };
}

export function validateSchedule({ contractId, month }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const datetime = moment().format('YYYY-MM-DD HH:mm:ss');
      await socket.put('/validate/schedule', {
        contractId,
        month,
        datetime
      });
      dispatch(slice.actions.validateSchedule(datetime));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
